import React from 'react';
import { Play } from 'lucide-react';

interface VideoPlayerProps {
  src: string;
  isVisible: boolean;
  isPlaying?: boolean;
  className?: string;
  onPlayingChange?: (playing: boolean) => void;
  isMuted?: boolean;
  onTimeUpdate?: (time: number) => void;
  initialTime?: number;
  objectFit?: 'contain' | 'cover';
}

export function VideoPlayer({
  src,
  isVisible,
  isPlaying: controlledIsPlaying,
  isMuted = true,
  className = '',
  onPlayingChange,
  onTimeUpdate,
  initialTime = 0,
  objectFit = 'cover'
}: VideoPlayerProps) {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [internalIsPlaying, setInternalIsPlaying] = React.useState(true);
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [isLoadError, setIsLoadError] = React.useState(false);
  const [isPending, setIsPending] = React.useState(false);
  const playPromiseRef = React.useRef<Promise<void> | null>(null);
  const lastTimeRef = React.useRef(0);
  const clickTimeRef = React.useRef<number>(0);
  
  const isPlaying = controlledIsPlaying ?? internalIsPlaying;
  const setIsPlaying = (playing: boolean) => {
    setInternalIsPlaying(playing);
    onPlayingChange?.(playing);
  };

  // Validate and process video source
  const videoSrc = React.useMemo(() => {
    // Return a fallback video if src is empty or invalid
    if (!src || src.trim() === '') {
      return 'https://storage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4';
    }
    
    // Return the original URL if already a working MP4
    if (src.match(/\.(mp4|webm|ogg)(\?.*)?$/i)) {
      return src;
    }
    
    // Check for specific domain where videos are hosted and ensure it's an MP4
    if (src.includes('r2.iphimedeia.com')) {
      if (!src.endsWith('.mp4')) {
        return `${src}.mp4`;
      }
    }
    
    return src;
  }, [src]);

  // Handle video errors
  const handleVideoError = React.useCallback(() => {
    if (!videoRef.current) return;
    
    console.error('Video loading error:', videoRef.current.src, videoRef.current.error);
    setIsLoadError(true);
    setIsLoaded(false);
    
    // Don't try to set empty src
    if (videoRef.current.src === '') {
      videoRef.current.src = 'https://storage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4';
      videoRef.current.load();
    }
  }, []);

  // Safely play the video with promise handling
  const safePlay = React.useCallback(async () => {
    if (!videoRef.current || isPending) return;
    
    try {
      setIsPending(true);
      
      // Make sure any previous play promise is resolved before proceeding
      if (playPromiseRef.current) {
        try {
          await playPromiseRef.current;
        } catch (e) {
          console.warn('Previous play promise rejected:', e);
        }
        playPromiseRef.current = null;
      }
      
      // Don't try to play if video has empty src
      if (!videoRef.current.src || videoRef.current.src === '') {
        console.error('Cannot play video with empty src');
        return;
      }
      
      // Don't try to play if already playing
      if (!videoRef.current.paused) {
        setIsPlaying(true);
        setIsPending(false);
        return;
      }
      
      console.log('Attempting to play video');
      playPromiseRef.current = videoRef.current.play();
      
      try {
        await playPromiseRef.current;
        setIsPlaying(true);
      } catch (error) {
        console.error('Play failed:', error);
        setIsPlaying(false);
      } finally {
        playPromiseRef.current = null;
      }
    } catch (error) {
      console.error('Play operation error:', error);
      setIsPlaying(false);
    } finally {
      setIsPending(false);
    }
  }, [setIsPlaying, isPending]);

  // Safely pause the video with promise handling
  const safePause = React.useCallback(async () => {
    if (!videoRef.current || isPending) return;
    
    try {
      setIsPending(true);
      
      // Make sure any previous play promise is resolved before pausing
      if (playPromiseRef.current) {
        try {
          await playPromiseRef.current;
        } catch (e) {
          console.warn('Previous play promise rejected during pause:', e);
        }
        playPromiseRef.current = null;
      }
      
      // Only pause if not already paused
      if (!videoRef.current.paused) {
        videoRef.current.pause();
      }
      
      setIsPlaying(false);
    } catch (error) {
      console.error('Pause operation error:', error);
    } finally {
      setIsPending(false);
    }
  }, [setIsPlaying, isPending]);

  // Initialize video when the component mounts or src changes
  React.useEffect(() => {
    if (!videoRef.current) return;
    
    // Don't set empty src
    if (videoSrc && videoSrc.trim() !== '') {
      videoRef.current.src = videoSrc;
      videoRef.current.muted = isMuted;
      videoRef.current.load();
      console.log('Video initialized with src:', videoSrc);
    } else {
      console.error('Attempted to set empty src');
    }
    
    // Cleanup on unmount or src change
    return () => {
      if (videoRef.current) {
        videoRef.current.pause();
        // Clear src only if component is unmounting
        if (!videoSrc) {
          videoRef.current.removeAttribute('src');
          videoRef.current.load();
        }
      }
    };
  }, [videoSrc, isMuted]);

  // Handle visibility changes
  React.useEffect(() => {
    if (!videoRef.current) return;
    
    const handleVisibilityChange = async () => {
      if (isVisible) {
        if (isPlaying && !isPending) {
          console.log('Video became visible, playing');
          await safePlay();
        }
      } else {
        console.log('Video became invisible, pausing');
        await safePause();
      }
    };
    
    // Queue the visibility handler to avoid immediate play/pause conflicts
    const timeoutId = setTimeout(handleVisibilityChange, 50);
    return () => clearTimeout(timeoutId);
  }, [isVisible, isPlaying, safePlay, safePause, isPending]);

  // Handle controlled playing state changes
  React.useEffect(() => {
    if (controlledIsPlaying === undefined || !videoRef.current) return;
    
    const handlePlayingChange = async () => {
      console.log('Controlled playing state changed:', controlledIsPlaying);
      if (controlledIsPlaying && isVisible) {
        await safePlay();
      } else {
        await safePause();
      }
    };
    
    // Queue the handler to avoid immediate play/pause conflicts
    const timeoutId = setTimeout(handlePlayingChange, 50);
    return () => clearTimeout(timeoutId);
  }, [controlledIsPlaying, safePlay, safePause, isVisible]);

  const togglePlay = async () => {
    if (isPending) return;
    
    if (isPlaying) {
      await safePause();
    } else {
      await safePlay();
    }
  };

  const handleVideoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Prevent double clicks
    const now = Date.now();
    if (now - clickTimeRef.current < 300) {
      return;
    }
    clickTimeRef.current = now;
    togglePlay();
  };

  // Set initial time when the video is loaded
  React.useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    
    const handleLoadedMetadata = () => {
      if (initialTime > 0) {
        video.currentTime = initialTime;
      }
    };
    
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    return () => video.removeEventListener('loadedmetadata', handleLoadedMetadata);
  }, [initialTime]);

  // Track video time updates
  React.useEffect(() => {
    const video = videoRef.current;
    if (!video || !onTimeUpdate) return;

    const handleTimeUpdate = () => {
      const currentTime = video.currentTime;
      if (Math.abs(currentTime - lastTimeRef.current) >= 0.1) {
        onTimeUpdate(currentTime);
        lastTimeRef.current = currentTime;
      }
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    return () => video.removeEventListener('timeupdate', handleTimeUpdate);
  }, [onTimeUpdate]);

  // Update muted state
  React.useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted]);

  // Handle video loaded state
  React.useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoaded = () => {
      console.log('Video loaded successfully');
      setIsLoaded(true);
      setIsLoadError(false);
    };

    const handleError = () => {
      console.error('Video error event:', video.error);
      setIsLoadError(true);
      setIsLoaded(false);
      setIsPlaying(false);
    };

    video.addEventListener('loadeddata', handleLoaded);
    video.addEventListener('error', handleError);
    
    return () => {
      video.removeEventListener('loadeddata', handleLoaded);
      video.removeEventListener('error', handleError);
    };
  }, [setIsPlaying]);

  return (
    <div className={`relative w-full h-full ${className}`}>
      <video
        playsInline
        webkit-playsinline="true"
        x-webkit-airplay="deny"
        ref={videoRef}
        className={`absolute inset-0 w-full h-full object-${objectFit} bg-black`}
        loop
        muted={isMuted}
        preload="auto"
        onClick={handleVideoClick}
        onError={handleVideoError}
        style={{
          WebkitMediaControls: 'none',
          MozMediaControls: 'none',
          controlsList: 'nodownload noplaybackrate nofullscreen noremoteplayback',
        }}
        controlsList="nodownload noplaybackrate nofullscreen noremoteplayback"
      />
      
      {/* Large centered play button overlay when paused */}
      {!isPlaying && isVisible && (
        <div className="absolute inset-0 flex items-center justify-center">
          <button
            onClick={handleVideoClick}
            className="p-6 rounded-full transition-all transform hover:scale-110 bg-black/20 backdrop-blur-sm"
          >
            <Play className="w-16 h-16 text-white/50" />
          </button>
        </div>
      )}
      
      {/* Loading indicator */}
      {!isLoaded && isVisible && !isLoadError && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/60">
          <div className="w-12 h-12 border-4 border-blue-600/20 border-t-blue-600 rounded-full animate-spin"></div>
        </div>
      )}
      
      {/* Loading error message */}
      {isLoadError && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80">
          <div className="text-center p-4">
            <p className="text-red-400 font-medium mb-2">Video loading failed</p>
            <p className="text-gray-400 text-sm">Unable to load video content</p>
            <button 
              onClick={() => {
                setIsLoadError(false);
                if (videoRef.current) {
                  // Ensure we have a valid src
                  if (!videoRef.current.src || videoRef.current.src === '') {
                    videoRef.current.src = 'https://storage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4';
                  }
                  videoRef.current.load();
                  safePlay();
                }
              }}
              className="mt-4 px-4 py-2 bg-blue-600 text-white text-sm rounded-full"
            >
              Retry
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default VideoPlayer;